/** WhatsApp per client (B04-001 onboarding into the client tenant, B04-002 usage + add-on limits) and access requests (B09-003). */
import { useState } from 'react';
import { Link } from 'react-router';
import { Button, Heading, Text, ToneChip } from '@qm/ui';
import { Icon } from '../../../shell/Icon';
import { GRANT_SCOPES, DEFAULT_AGENCY_SCOPES } from '@qm/contracts/partners';
import { useSurface } from '../kit/context';
import { useAction, useResource } from '../kit/resource';
import { Badge, Checkbox, DataTable, ErrorNote, Field, Loadable, Money, Notice, Screen, When } from '../kit/ui';
import { usageState } from '../lib/logic';
import { INBOX_TABS, SAMPLE_CONVERSATIONS, avatarTone, filterConversations, inboxCounts, initials, maskPhone, type InboxTab } from '../lib/views';
import { usePartnerApi } from './layout';

export function WhatsAppPage() {
  const { t } = useSurface();
  const api = usePartnerApi();
  const numbers = useResource('wa:numbers', api.waNumbers);
  const usage = useResource('wa:usage', api.waUsage);
  const clients = useResource('clients:wa', () => api.clients({ tab: 'active', limit: 100 }).then((e) => e.data));
  const [client, setClient] = useState('');
  const start = useAction(async (id: string) => {
    const r = await api.waStartOnboarding(id);
    if (typeof window !== 'undefined' && r.signupUrl.startsWith('https://')) window.location.assign(r.signupUrl);
    return r;
  });
  return (
    <Screen title={t('portal.partners.wa.title')} testId="whatsapp">
      <Text tone="muted">{t('portal.partners.wa.ownership')}</Text>
      <InboxPreview />
      <Loadable resource={clients}>
        {(cl) => (
          <div className="qx-toolbar">
            <Field label={t('portal.partners.wa.onboardFor')}>
              {(id) => <select id={id} value={client} onChange={(e) => setClient(e.target.value)}><option value="">{t('portal.partners.ads.chooseClient')}</option>{cl.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}</select>}
            </Field>
            <Button variant="primary" disabled={!client || start.pending} onClick={() => void start.run(client)}>{t('portal.partners.wa.startSignup')}</Button>
          </div>
        )}
      </Loadable>
      <ErrorNote error={start.error} />
      <Loadable resource={numbers}>
        {(rows) => <DataTable caption={t('portal.partners.wa.numbers')} rows={rows} rowKey={(n) => n.id} empty={t('portal.partners.wa.noNumbers')} columns={[
          { key: 'phone', header: t('portal.partners.wa.number'), render: (n) => <bdi>{n.displayPhone}</bdi> },
          { key: 'client', header: t('portal.partners.wa.tenant'), render: (n) => <span data-testid="wa-tenant">{n.clientName}</span> },
          { key: 'status', header: t('portal.partners.clients.col.status'), render: (n) => <Badge tone={n.status === 'connected' ? 'good' : n.status === 'pending' ? 'warn' : 'bad'}>{t(`portal.partners.wa.${n.status}`)}</Badge> },
        ]} />}
      </Loadable>
      <Loadable resource={usage}>
        {(rows) => <DataTable caption={t('portal.partners.wa.usage')} rows={rows} rowKey={(u) => u.clientTenantId} empty={t('portal.kit.empty')} columns={[
          { key: 'client', header: t('portal.partners.clients.col.name'), render: (u) => u.clientName },
          { key: 'addon', header: t('portal.partners.wa.addon'), render: (u) => (u.addon ? <Badge tone="good">{t('portal.partners.wa.addonOn')}</Badge> : <Badge>{t('portal.partners.wa.addonOff')}</Badge>) },
          { key: 'msgs', header: t('portal.partners.wa.messages'), render: (u) => `${u.messages}${u.messageLimit ? ` / ${u.messageLimit}` : ''}` },
          { key: 'ai', header: t('portal.partners.wa.aiCost'), render: (u) => <><Money minor={u.aiCostMinor} currency={u.currency} />{u.aiCapMinor ? <> / <Money minor={u.aiCapMinor} currency={u.currency} /></> : null}</> },
          {
            key: 'state', header: t('portal.partners.wa.limit'), render: (u) => {
              const s = usageState(u);
              return s.state === 'reached'
                ? <Badge tone="bad" testId="wa-limit-reached">{t('portal.partners.wa.limitReached')}</Badge>
                : s.state === 'near' ? <Badge tone="warn">{t('portal.partners.wa.limitNear')}</Badge> : <Badge tone="good">{t('portal.partners.wa.limitOk')}</Badge>;
            },
          },
        ]} />}
      </Loadable>
    </Screen>
  );
}

/** Partner side of B09-003: ask a customer workspace for access with explicit scopes; see grant state. */
export function AccessPage() {
  const { t } = useSurface();
  const api = usePartnerApi();
  const list = useResource('grants', api.accessRequests);
  const [tenantId, setTenantId] = useState('');
  const [scopes, setScopes] = useState<string[]>([...DEFAULT_AGENCY_SCOPES]);
  const req = useAction(() => api.requestAccess(tenantId.trim(), scopes));
  return (
    <Screen title={t('portal.partners.access.title')} subtitle={t('portal.partners.access.subtitle')} testId="access">
      <form onSubmit={async (e) => { e.preventDefault(); if (await req.run()) list.reload(); }}>
        <Field label={t('portal.partners.access.workspaceId')}>{(id) => <input id={id} name="tenantId" value={tenantId} onChange={(e) => setTenantId(e.target.value)} />}</Field>
        {GRANT_SCOPES.map((s) => (
          <Checkbox key={s} label={t(`partners.grants.scope.${s.replace(':', '_')}`)} checked={scopes.includes(s)} onChange={(v) => setScopes((x) => (v ? [...x, s] : x.filter((y) => y !== s)))} />
        ))}
        <Button type="submit" variant="primary" disabled={!tenantId.trim() || scopes.length === 0 || req.pending}>{t('portal.partners.access.request')}</Button>
      </form>
      <ErrorNote error={req.error} />
      {req.result ? <Notice>{t('portal.partners.access.requested')}</Notice> : null}
      <Loadable resource={list}>
        {(rows) => <DataTable rows={rows} rowKey={(g) => g.id} empty={t('portal.kit.empty')} columns={[
          { key: 'ws', header: t('portal.partners.access.workspace'), render: (g) => g.tenantName ?? g.tenantId },
          { key: 'scopes', header: t('portal.partners.access.scopes'), render: (g) => g.scopes.map((s) => t(`partners.grants.scope.${s.replace(':', '_')}`)).join(', ') },
          { key: 'status', header: t('portal.partners.clients.col.status'), render: (g) => <Badge tone={g.status === 'active' ? 'good' : g.status === 'requested' ? 'warn' : 'bad'} testId={`grant-${g.status}`}>{t(`portal.partners.access.status.${g.status}`)}</Badge> },
          { key: 'at', header: t('portal.partners.audit.time'), narrow: 'hide', render: (g) => <When iso={g.createdAt} /> },
        ]} />}
      </Loadable>
    </Screen>
  );
}

/**
 * WhatsApp Inbox layout (list | thread | lead panel). The partner API has no conversation endpoint and customer conversations
 * stay in the customer workspace, so every row here is sample data, badged as such; its action buttons are disabled.
 */
export function InboxPreview() {
  const { t } = useSurface();
  const [tab, setTab] = useState<InboxTab>('open');
  const [picked, setPicked] = useState<string | null>(null);
  const [status, setStatus] = useState('new');
  const counts = inboxCounts(SAMPLE_CONVERSATIONS);
  const rows = filterConversations(SAMPLE_CONVERSATIONS, tab);
  const sel = rows.find((c) => c.id === picked) ?? rows[0] ?? null;
  return (
    <section className="qp-stack" aria-labelledby="wa-inbox" data-testid="wa-inbox">
      <div className="qp-head">
        <Heading level={2} id="wa-inbox">{t('portal.partners.inbox.title')}</Heading>
        <span className="qp-sample" data-testid="sample-badge">{t('portal.partners.inbox.sample')}</span>
      </div>
      <Text tone="muted" className="qp-inbox-note">{t('portal.partners.inbox.sampleNote')}</Text>
      <div className="qs-inbox">
        <div>
          <div className="qp-inbox__tabs" role="tablist" aria-label={t('portal.partners.inbox.list')}>
            {INBOX_TABS.map((k) => (
              <button key={k} type="button" role="tab" aria-selected={tab === k} data-tab={k} onClick={() => { setTab(k); setPicked(null); }}>{t(`portal.partners.inbox.tab.${k}`)} {counts[k]}</button>
            ))}
          </div>
          <ul className="qs-inbox__list" aria-label={t('portal.partners.inbox.list')}>
            {rows.map((c, i) => (
              <li key={c.id}>
                <button type="button" className="qs-inbox__row" aria-current={sel?.id === c.id ? 'true' : undefined} onClick={() => setPicked(c.id)}>
                  <span className="qs-avatar" data-tone={avatarTone(i)} aria-hidden="true">{initials(t(c.titleKey))}</span>
                  <span className="qp-inbox__row-body"><b>{t(c.titleKey)}</b><small>{t(c.previewKey)}</small>{c.aiDraft ? <ToneChip tone="lavender">{t('portal.partners.inbox.aiDraft')}</ToneChip> : null}</span>
                  <span className="qp-inbox__time">{c.time}</span>
                </button>
              </li>
            ))}
            {rows.length === 0 ? <li className="qp-queue-empty"><Text tone="muted">{t('portal.kit.empty')}</Text></li> : null}
          </ul>
        </div>
        {sel ? (
          <div className="qs-inbox__thread">
            {sel.aiHandling ? (
              <div className="qs-aibar"><Icon name="settings" size={18} /><span>{t('portal.partners.inbox.aiHandling')}</span><Link to="/mcp">{t('portal.partners.inbox.viewAgent')}</Link></div>
            ) : null}
            <div className="qp-bubbles">
              <div className="qp-bubble" data-from="them"><p>{t(sel.previewKey)}</p></div>
              <div className="qp-bubble" data-from="us"><p>{t('portal.partners.inbox.sample.reply')}</p></div>
            </div>
            {sel.aiDraft ? (
              <div className="qs-draft" data-testid="ai-draft">
                <b>{t('portal.partners.inbox.draftTitle')}</b>
                <p>{t('portal.partners.inbox.sample.draft')}</p>
                <div className="qs-draft__actions">
                  <Button variant="primary" disabled>{t('portal.partners.inbox.approve')}</Button>
                  <Button disabled>{t('portal.partners.inbox.edit')}</Button>
                  <Button disabled>{t('portal.partners.inbox.delete')}</Button>
                </div>
              </div>
            ) : null}
            <div className="qp-composer">
              <input aria-label={t('portal.partners.inbox.compose')} placeholder={t('portal.partners.inbox.compose')} disabled />
              <Button disabled>{t('portal.partners.inbox.send')}</Button>
            </div>
          </div>
        ) : <div className="qs-inbox__thread"><Text tone="muted">{t('portal.kit.empty')}</Text></div>}
        {sel ? (
          <aside className="qs-inbox__lead qp-lead" aria-label={t('portal.partners.inbox.lead')}>
            <div><h3>{t(sel.titleKey)}</h3><span className="qp-muted">{sel.lead.id} <bdi>{maskPhone(sel.lead.phone)}</bdi></span></div>
            <Field label={t('portal.partners.inbox.status')}>
              {(id) => <select id={id} value={status} onChange={(e) => setStatus(e.target.value)}>{(['new', 'qualified', 'nurture'] as const).map((k) => <option key={k} value={k}>{t(`portal.partners.inbox.status.${k}`)}</option>)}</select>}
            </Field>
            <h3>{t('portal.partners.inbox.qualification')}</h3>
            <dl>
              {(['intent', 'budget', 'timeframe', 'need', 'fit'] as const).map((k) => <div key={k}><dt>{t(`portal.partners.inbox.q.${k}`)}</dt><dd>{t(`portal.partners.inbox.v.${sel.lead.qualification[k]}`)}</dd></div>)}
            </dl>
            <h3>{t('portal.partners.inbox.aiControls')}</h3>
            <Button disabled>{t('portal.partners.inbox.takeOver')}</Button>
            <Button disabled>{t('portal.partners.inbox.assign')}</Button>
            <Button disabled>{t('portal.partners.inbox.createOpp')}</Button>
          </aside>
        ) : null}
      </div>
    </section>
  );
}
